import type { PersistedDtoolsSiAttempt } from '@/lib/dtools-si-import-attempt'
import { getDtoolsSiImportRecoveryAction } from '@/lib/api/dtools-si-constants'
import { isAxiosError } from 'axios'
import { z } from 'zod'

const importStatusSchema = z.object({
  status: z.enum(['not_found', 'pending', 'imported', 'imported_ack_pending', 'failed', 'unknown']),
  quote_id: z.string(),
  si_project_id: z.string(),
  sync_log_id: z.string().nullable(),
  error: z.string().nullable(),
})

export type RecoveredDtoolsSiImport = {
  status: 'imported' | 'imported_ack_pending'
  quote_id: string
  sync_log_id: string
  si_project_id: string
  replaced_existing: boolean
  completion_recovered: true
}

export class DtoolsSiImportStatusUnresolvedError extends Error {
  constructor() {
    super('D-Tools SI import status is unresolved. Do not retry; refresh later or contact support.')
    this.name = 'DtoolsSiImportStatusUnresolvedError'
  }
}

export class DtoolsSiImportFailedError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'DtoolsSiImportFailedError'
  }
}

type RecoveryDependencies = {
  requestStatus: (attempt: PersistedDtoolsSiAttempt, timeoutMs: number) => Promise<unknown>
  now?: () => number
  sleep?: (delayMs: number) => Promise<void>
  random?: () => number
  signal?: AbortSignal
}

function throwIfAborted(signal?: AbortSignal) {
  if (signal?.aborted) throw signal.reason ?? new DOMException('Aborted', 'AbortError')
}

function sleepWithSignal(delayMs: number, signal?: AbortSignal) {
  if (!signal) return new Promise<void>((resolve) => setTimeout(resolve, delayMs))
  throwIfAborted(signal)
  return new Promise<void>((resolve, reject) => {
    const onAbort = () => {
      clearTimeout(timeoutId)
      reject(signal.reason ?? new DOMException('Aborted', 'AbortError'))
    }
    const timeoutId = setTimeout(() => {
      signal.removeEventListener('abort', onAbort)
      resolve()
    }, delayMs)
    signal.addEventListener('abort', onAbort, { once: true })
  })
}

function retryAfterMs(error: unknown, now: number): number | null {
  if (!isAxiosError(error) || error.response?.status !== 429) return null
  const raw = error.response.headers?.['retry-after']
  if (typeof raw !== 'string' && typeof raw !== 'number') return null
  const seconds = Number(raw)
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1_000)
  const date = Date.parse(String(raw))
  return Number.isFinite(date) ? Math.max(0, date - now) : null
}

export async function recoverDtoolsSiImportAttempt(
  attempt: PersistedDtoolsSiAttempt,
  dependencies: RecoveryDependencies
): Promise<RecoveredDtoolsSiImport> {
  const now = dependencies.now ?? Date.now
  const sleep = dependencies.sleep ?? ((delayMs) => sleepWithSignal(delayMs, dependencies.signal))
  const random = dependencies.random ?? Math.random
  let delayMs = 2_000
  let firstRequest = true

  while (firstRequest || now() < attempt.recoveryDeadlineAt) {
    throwIfAborted(dependencies.signal)
    firstRequest = false
    let serverDelayMs: number | null = null
    try {
      const remaining = attempt.recoveryDeadlineAt - now()
      const requestTimeoutMs = remaining > 0 ? Math.min(15_000, remaining) : 15_000
      const raw = await dependencies.requestStatus(attempt, requestTimeoutMs)
      throwIfAborted(dependencies.signal)
      const status = importStatusSchema.parse(raw)
      const action = getDtoolsSiImportRecoveryAction(status.status)
      if ((action === 'succeed' || action === 'succeed_with_warning') && status.sync_log_id) {
        return {
          status: status.status === 'imported_ack_pending' ? 'imported_ack_pending' : 'imported',
          quote_id: attempt.quoteId,
          sync_log_id: status.sync_log_id,
          si_project_id: attempt.siProjectId,
          replaced_existing: attempt.replaceExisting,
          completion_recovered: true,
        }
      }
      if (action === 'fail') throw new DtoolsSiImportFailedError(status.error || 'D-Tools SI import failed.')
    } catch (error) {
      throwIfAborted(dependencies.signal)
      if (!isAxiosError(error)) throw error
      const status = error.response?.status ?? 0
      if ([401, 403].includes(status)) throw error
      if (status >= 400 && status < 500 && status !== 429) throw error
      serverDelayMs = retryAfterMs(error, now())
    }

    const remaining = attempt.recoveryDeadlineAt - now()
    if (remaining <= 0) break
    const jitteredDelay = delayMs * (0.8 + random() * 0.4)
    await sleep(Math.min(serverDelayMs ?? jitteredDelay, remaining))
    throwIfAborted(dependencies.signal)
    delayMs = Math.min(15_000, delayMs * 1.7)
  }
  throw new DtoolsSiImportStatusUnresolvedError()
}
